// pages/strategic-playbook/tools/drawdown-test.js
// ════════════════════════════════════════════════════
// DRAWDOWN TOLERANCE TEST
// ════════════════════════════════════════════════════
// Reverse-engineers the right position size from your
// honest psychological capacity, not your ambition.
//
// "Position too big? At what drawdown would you actually
// panic-sell? Reduce to that ceiling."
//
// Connects to: Marks (cycles) + Resilience pillar
// ════════════════════════════════════════════════════

import { useState, useMemo } from 'react';
import Head from 'next/head';
import Link from 'next/link';

export default function DrawdownTest() {
  const [accountSize, setAccountSize] = useState(250000);
  const [proposedPosition, setProposedPosition] = useState(50000);
  const [historicalDrawdown, setHistoricalDrawdown] = useState(35);
  const [actualHeld, setActualHeld] = useState(15);
  const [horizonYears, setHorizonYears] = useState(5);
  const [recoveryMonthsAvg, setRecoveryMonthsAvg] = useState(18);

  const result = useMemo(() => {
    return analyze({
      accountSize,
      proposedPosition,
      historicalDrawdown: historicalDrawdown / 100,
      actualHeld: actualHeld / 100,
      horizonYears,
      recoveryMonthsAvg,
    });
  }, [accountSize, proposedPosition, historicalDrawdown, actualHeld, horizonYears, recoveryMonthsAvg]);

  return (
    <div className="sp-root">
      <Head>
        <title>Drawdown Tolerance Test · Strategic Intelligence</title>
      </Head>

      <div className="sp-page">
        <ToolTopbar />

        <div style={{ marginBottom: 32 }}>
          <Link href="/strategic-playbook/tools" className="sp-reader-back" style={{ marginBottom: 16 }}>
            ← Tools
          </Link>
          <div style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.20em', color: 'var(--sp-aged-brass)', marginBottom: 14 }}>
            Decision Sizing · Calculator
          </div>
          <h1 style={{ fontFamily: 'var(--sp-font-serif)', fontWeight: 500, fontSize: 32, lineHeight: 1.2, color: 'var(--sp-warm-cream)', margin: '0 0 14px 0', letterSpacing: '-0.01em' }}>
            Drawdown Tolerance Test
          </h1>
          <p style={{ fontSize: 14, lineHeight: 1.65, color: 'rgba(232, 221, 196, 0.78)', maxWidth: 700, margin: 0 }}>
            Honest psychological-capacity test. Most position sizing is set by ambition.
            This tool reverse-engineers from your <strong>actual</strong> drawdown
            history. The position you can hold through panic compounds; the position
            you panic-sell loses both money AND the recovery that would have followed.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.3fr', gap: 24 }} className="dd-grid">
          {/* INPUTS */}
          <div style={{ padding: '28px', background: 'var(--sp-soft-charcoal)', border: '1px solid var(--sp-divider)', borderRadius: 'var(--sp-radius-card)' }}>
            <h3 style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.18em', color: 'var(--sp-aged-brass)', margin: '0 0 22px 0' }}>
              Inputs
            </h3>

            <Field label="Account size" suffix="$">
              <NumberInput value={accountSize} onChange={setAccountSize} step={5000} min={0} />
            </Field>

            <Field label="Proposed position size" suffix="$">
              <NumberInput value={proposedPosition} onChange={setProposedPosition} step={1000} min={0} />
            </Field>

            <Field label="Historical drawdown for this asset class" suffix="% loss" hint="Worst drawdown in 30+ year history. S&P: -50%. Tech-heavy: -75%. Crypto: -85%.">
              <NumberInput value={historicalDrawdown} onChange={setHistoricalDrawdown} step={5} min={5} max={100} />
            </Field>

            <Field
              label="Maximum drawdown YOU have actually held through"
              suffix="% loss"
              hint="Be brutally honest. Not what you THINK you'd hold. What you ACTUALLY did."
            >
              <NumberInput value={actualHeld} onChange={setActualHeld} step={1} min={0} max={100} />
            </Field>

            <Field label="Time horizon for this position" suffix="yr">
              <NumberInput value={horizonYears} onChange={setHorizonYears} step={1} min={0.5} max={30} />
            </Field>

            <Field label="Average recovery time" suffix="months" hint="From bottom to new high in this asset class.">
              <NumberInput value={recoveryMonthsAvg} onChange={setRecoveryMonthsAvg} step={1} min={1} max={120} />
            </Field>
          </div>

          {/* OUTPUTS */}
          <div>
            {/* Verdict card */}
            <div style={{ padding: '28px', background: 'linear-gradient(135deg, var(--sp-card-charcoal) 0%, var(--sp-soft-charcoal) 100%)', border: `1px solid ${result.verdictColor}`, borderRadius: 'var(--sp-radius-card)', marginBottom: 16 }}>
              <div style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.18em', color: result.verdictColor, marginBottom: 14 }}>
                Verdict
              </div>
              <div style={{ fontFamily: 'var(--sp-font-serif)', fontSize: 28, lineHeight: 1.15, color: 'var(--sp-warm-cream)', marginBottom: 14, letterSpacing: '-0.01em' }}>
                {result.verdictHeadline}
              </div>
              <div style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 13, color: result.verdictColor, marginBottom: 18, letterSpacing: '0.04em' }}>
                Recommended position: <strong>${result.recommendedSize.toLocaleString()}</strong> ({result.recommendedPct.toFixed(2)}%)
              </div>
              <div style={{ fontFamily: 'var(--sp-font-serif)', fontStyle: 'italic', fontSize: 14, lineHeight: 1.6, color: 'var(--sp-warm-cream)', borderLeft: `2px solid ${result.verdictColor}`, paddingLeft: 14 }}>
                {result.verdictBody}
              </div>
            </div>

            {/* Scenario table */}
            <div style={{ padding: '24px 28px', background: 'var(--sp-soft-charcoal)', border: '1px solid var(--sp-divider)', borderRadius: 'var(--sp-radius-card)', marginBottom: 16 }}>
              <h3 style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.18em', color: 'var(--sp-aged-brass)', margin: '0 0 18px 0' }}>
                Scenario Analysis
              </h3>
              <ResultRow label="Position as % of account" value={result.proposedPct.toFixed(2) + '%'} />
              <ResultRow
                label={`If asset drops ${historicalDrawdown}% (historical worst)`}
                value={'-$' + result.maxLoss.toLocaleString()}
                color="var(--sp-oxblood)"
              />
              <ResultRow
                label="Account drawdown at that loss"
                value={'-' + result.accountDrawdownAtMax.toFixed(2) + '%'}
                color={result.accountDrawdownAtMax > actualHeld ? 'var(--sp-oxblood)' : 'var(--sp-forest-moss)'}
                hint={result.accountDrawdownAtMax > actualHeld ? 'Above your tolerance' : 'Within your tolerance'}
              />
              <ResultRow
                label="Months to recover at average recovery rate"
                value={result.recoveryMonths.toFixed(0) + ' months'}
              />
              <ResultRow
                label="Position size that fits YOUR tolerance"
                value={'$' + result.recommendedSize.toLocaleString()}
                highlight
              />
            </div>

            {/* The hard truth */}
            <div style={{ padding: '20px 24px', background: result.tooLarge ? 'rgba(74, 31, 31, 0.18)' : 'var(--sp-parchment-bg)', border: `1px solid ${result.tooLarge ? 'rgba(74, 31, 31, 0.40)' : 'var(--sp-divider)'}`, borderRadius: 'var(--sp-radius-card)' }}>
              <div style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.16em', color: result.tooLarge ? 'var(--sp-oxblood)' : 'var(--sp-aged-brass)', marginBottom: 10 }}>
                {result.tooLarge ? 'The Hard Truth' : 'Honest Read'}
              </div>
              <div style={{ fontSize: 13, lineHeight: 1.6, color: 'rgba(232, 221, 196, 0.78)' }}>
                {result.honestRead}
              </div>
            </div>

            <div style={{ marginTop: 16, fontSize: 12, color: 'rgba(232, 221, 196, 0.55)' }}>
              <span style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'rgba(232, 221, 196, 0.45)', marginRight: 8 }}>
                Connected dossiers:
              </span>
              <Link href="/strategic-playbook/marks-second-level-thinking" style={{ color: 'var(--sp-aged-brass)' }}>Marks</Link>
              {' · '}
              <Link href="/strategic-playbook/pandemic-shock-playbook" style={{ color: 'var(--sp-aged-brass)' }}>Pandemic Playbook (cascade survival)</Link>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @media (max-width: 900px) {
          .dd-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
}

function analyze({ accountSize, proposedPosition, historicalDrawdown, actualHeld, horizonYears, recoveryMonthsAvg }) {
  const proposedPct = (proposedPosition / accountSize) * 100;
  const maxLoss = Math.round(proposedPosition * historicalDrawdown);
  const accountDrawdownAtMax = (maxLoss / accountSize) * 100;
  const tooLarge = accountDrawdownAtMax > actualHeld * 100;

  // Recommended position: max position such that worst drawdown <= actual held tolerance
  const maxAccountDrawdownTolerance = actualHeld;
  const recommendedSize = Math.floor((accountSize * maxAccountDrawdownTolerance) / historicalDrawdown);
  const recommendedPct = (recommendedSize / accountSize) * 100;
  const reduction = proposedPosition > 0 ? (1 - recommendedSize / proposedPosition) * 100 : 0;

  // Recovery months scaled to actual proposed loss (proportional to drawdown depth)
  const recoveryMonths = recoveryMonthsAvg * (historicalDrawdown / 0.40);

  // Verdict
  let verdictHeadline, verdictBody, verdictColor, honestRead;

  if (tooLarge && reduction > 50) {
    verdictHeadline = 'Position too large — reduce significantly.';
    verdictBody = `At full drawdown, your position would lose $${maxLoss.toLocaleString()} (${accountDrawdownAtMax.toFixed(1)}% of account). You've only actually held through ${(actualHeld * 100).toFixed(0)}% drawdowns. Reduce to $${recommendedSize.toLocaleString()} (${reduction.toFixed(0)}% smaller).`;
    verdictColor = 'var(--sp-oxblood)';
    honestRead = `Brutal honesty: position size set by ambition, not capacity. If you size at $${proposedPosition.toLocaleString()} and the asset drops ${(historicalDrawdown * 100).toFixed(0)}%, history says you'll panic-sell near the bottom. You'll lock in the loss AND miss the recovery. The recommended size lets you hold through worst-case AND keep compounding through it.`;
  } else if (tooLarge) {
    verdictHeadline = 'Position somewhat oversized — consider reducing.';
    verdictBody = `Worst-case drawdown ${accountDrawdownAtMax.toFixed(1)}% is above your demonstrated tolerance ${(actualHeld * 100).toFixed(0)}%. Consider reducing to $${recommendedSize.toLocaleString()}.`;
    verdictColor = 'var(--sp-cognac)';
    honestRead = `Position is on the edge. If you have new tolerance evidence (you've held through similar before), maybe stretch. If not, the conservative path is reducing. The Marks framework: cycles dominate; defensive positioning beats trying to ride volatility you can't actually stomach.`;
  } else {
    verdictHeadline = 'Position fits your tolerance.';
    verdictBody = `Worst-case drawdown ${accountDrawdownAtMax.toFixed(1)}% is below your demonstrated tolerance of ${(actualHeld * 100).toFixed(0)}%. You can hold this.`;
    verdictColor = 'var(--sp-forest-moss)';
    honestRead = `Sized correctly relative to your demonstrated tolerance. Important caveat: tolerance can change. Health, family, career changes all reduce risk capacity. Re-test annually. The position you can hold today may be too large in 3 years.`;
  }

  return {
    proposedPct,
    maxLoss,
    accountDrawdownAtMax,
    recoveryMonths,
    recommendedSize,
    recommendedPct,
    tooLarge,
    verdictHeadline,
    verdictBody,
    verdictColor,
    honestRead,
  };
}

function Field({ label, suffix, hint, children }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.14em', color: 'rgba(232, 221, 196, 0.55)', marginBottom: 6, display: 'flex', justifyContent: 'space-between' }}>
        <span>{label}</span>
        {suffix && <span style={{ color: 'var(--sp-aged-brass)' }}>{suffix}</span>}
      </div>
      {children}
      {hint && <div style={{ fontSize: 11, color: 'rgba(232, 221, 196, 0.50)', marginTop: 4, lineHeight: 1.5 }}>{hint}</div>}
    </div>
  );
}

const inputStyle = {
  width: '100%',
  padding: '10px 14px',
  background: 'var(--sp-charcoal-black)',
  border: '1px solid var(--sp-divider)',
  borderRadius: 'var(--sp-radius)',
  color: 'var(--sp-warm-cream)',
  fontFamily: 'var(--sp-font-body)',
  fontSize: 14,
  outline: 'none',
};

function NumberInput({ value, onChange, step, min, max }) {
  return (
    <input
      type="number"
      value={value}
      step={step}
      min={min}
      max={max}
      onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
      style={inputStyle}
    />
  );
}

function ResultRow({ label, value, color, highlight, hint }) {
  return (
    <div style={{ padding: '8px 0', borderBottom: '1px solid rgba(58, 47, 40, 0.5)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 12, color: highlight ? 'var(--sp-warm-cream)' : 'rgba(232, 221, 196, 0.65)' }}>
          {label}
        </span>
        <span style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 13, color: color || (highlight ? 'var(--sp-aged-brass)' : 'var(--sp-warm-cream)'), fontWeight: highlight ? 600 : 400 }}>
          {value}
        </span>
      </div>
      {hint && <div style={{ fontSize: 10, color: 'rgba(232, 221, 196, 0.45)', marginTop: 2 }}>{hint}</div>}
    </div>
  );
}

function ToolTopbar() {
  return (
    <div className="sp-topbar">
      <div>
        <h1>Strategic Intelligence</h1>
        <div className="sp-topbar-meta">Tools · Drawdown Tolerance Test</div>
      </div>
      <nav className="sp-topbar-nav">
        <Link href="/strategic-playbook">Dashboard</Link>
        <Link href="/strategic-playbook/library">Library</Link>
        <Link href="/strategic-playbook/tools" className="sp-active">Tools</Link>
        <Link href="/strategic-playbook/decisions">Decisions</Link>
        <Link href="/strategic-playbook/simulate">Simulate</Link>
      </nav>
    </div>
  );
}
